import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export type OrgRole = "owner" | "admin" | "operator" | "viewer";

interface OrgInfo {
  id: string;
  name: string;
  slug: string;
}

interface OrgMembership {
  org: OrgInfo | null;
  role: OrgRole | null;
  loading: boolean;
  needsSetup: boolean;
  refresh: () => Promise<void>;
}

export const useOrg = (): OrgMembership => {
  const { user } = useAuth();
  const [org, setOrg] = useState<OrgInfo | null>(null);
  const [role, setRole] = useState<OrgRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [needsSetup, setNeedsSetup] = useState(false);

  const fetchOrg = useCallback(async () => {
    if (!user) {
      setOrg(null);
      setRole(null);
      setLoading(false);
      setNeedsSetup(false);
      return;
    }

    try {
      const { data: membership } = await supabase
        .from("org_members")
        .select("role, organizations(id, name, slug)")
        .eq("user_id", user.id)
        .maybeSingle();

      if (membership && (membership as any).organizations) {
        const orgData = (membership as any).organizations;
        setOrg({ id: orgData.id, name: orgData.name, slug: orgData.slug });
        setRole(membership.role as OrgRole);
        setNeedsSetup(false);
      } else {
        setOrg(null);
        setRole(null);
        setNeedsSetup(true);
      }
    } catch (e) {
      console.error("Failed to fetch org:", e);
      setNeedsSetup(true);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchOrg();
  }, [fetchOrg]);

  return { org, role, loading, needsSetup, refresh: fetchOrg };
};

export const useOrgRole = () => {
  const { role, loading } = useOrg();

  const hasRole = (check: OrgRole) => role === check;
  const hasAnyRole = (...check: OrgRole[]) => role !== null && check.includes(role);
  const isOwner = role === "owner";
  const isAdmin = role === "owner" || role === "admin";
  const isOperator = role === "owner" || role === "admin" || role === "operator";
  const isViewer = role !== null;

  return { role, loading, hasRole, hasAnyRole, isOwner, isAdmin, isOperator, isViewer };
};
